import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import { useApiPost } from '../../api/Api.hooks';
import { log } from '../../tools/LogHelper';
import { stringify } from 'flatted';

export interface FilePresignedDto {
  file: File;
  destinationUrl: string;
  signedUrl: string;
  expiration: number;
}

export const defaultPresignEndPoint = 'files/presign';
export function useSecureFileUrl(url?: string) {
  if (url) {
    const querySpacing = url.includes('?') ? '&' : '?';
    return `${url}${querySpacing}token=${api.getAccessToken()}`;
  }
}

export interface UploadFile {
  file: File;
  progress: number;
  isUploading: boolean;
  request: XMLHttpRequest;
  isCompleted?: boolean;
  isError?: boolean;
  presignedResult?: FilePresignedDto;
}
export function useUploadFiles(
  path: string,
  presignEndPoint = defaultPresignEndPoint
) {
  const {
    presignedResults,
    error: presignedError,
    presignUrls,
    clearData
  } = useCreatePresignedUrls(path, presignEndPoint);
  const fileIndexRef = useRef(0);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>();
  const [isUploading, setIsUploading] = useState(false);
  const upload = useCallback(
    (fileList: FileList | File[]) => {
      const files: UploadFile[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const uploadFile = {
          file: fileList[i],
          progress: 0,
          isUploading: true,
          request: new XMLHttpRequest(),
          isCompleted: false,
          isError: false
        };
        files.push(uploadFile);
      }
      // start upload (see first useEffect)
      setUploadFiles([...(uploadFiles || []), ...files]);
      presignUrls(files.map(u => u.file));
    },
    [presignUrls, uploadFiles]
  );
  // remove an upload file and abort if uploading
  const remove = useCallback(
    (file: File) => {
      if (uploadFiles) {
        const index = uploadFiles.findIndex(f => f.file === file);
        if (index > -1) {
          uploadFiles[index].request.abort();
          setUploadFiles([...(uploadFiles.filter(f => f.file !== file) || [])]);
          if (index <= fileIndexRef.current) {
            fileIndexRef.current--;
          }
        }
      } else {
        fileIndexRef.current = 0;
      }
    },
    [uploadFiles]
  );

  const removeAll = useCallback(() => {
    if (uploadFiles && uploadFiles?.length) {
      uploadFiles.map(uploadFile => uploadFile.request.abort());
      setUploadFiles([]);
      fileIndexRef.current = 0;
    }
  }, [uploadFiles]);
  // get pre-signed url for files
  useEffect(() => {
    clearData();
    if (
      uploadFiles?.length &&
      fileIndexRef.current < uploadFiles.length &&
      presignedResults?.length
    ) {
      // find presigned url matching the file to upload
      const uploadFile = uploadFiles[fileIndexRef.current];
      const presignedResult = uploadFile
        ? presignedResults?.find(p => p.file === uploadFile.file)
        : null;
      if (!presignedResult) return; // have not yet received presigned url
      uploadFile.presignedResult = presignedResult;
      setUploadFiles([...uploadFiles]);
      fileIndexRef.current++;
      // Perform Upload
      uploadFile.request.addEventListener('progress', event => {
        // console.log('uploadFile', uploadFile.progress);
        if (event.lengthComputable) {
          const progress = event.loaded / event.total;
          uploadFile.progress = progress;
          uploadFile.isUploading = progress !== 1;
          uploadFile.isCompleted = progress === 1;
          log('uploadFile', uploadFile.progress);
          setIsUploading(progress !== 1);
          setUploadFiles([...uploadFiles]);
        }
      });
      uploadFile.request.addEventListener('load', event => {
        // console.log(`uploadFile load`);
        uploadFile.progress = 1;
        uploadFile.isUploading = false;
        uploadFile.isCompleted = true;
        setUploadFiles([...uploadFiles]);
        setIsUploading(false);
      });
      uploadFile.request.addEventListener('error', event => {
        // console.log(`uploadFile error`, event);
        uploadFile.progress = 0;
        uploadFile.isUploading = false;
        uploadFile.isError = true;
        setUploadFiles([...uploadFiles]);
        setIsUploading(false);
      });
      log(
        'uploadFile.presignedResult.signedUrl',
        uploadFile.presignedResult.signedUrl
      );
      uploadFile.request.open('PUT', uploadFile.presignedResult.signedUrl);
      uploadFile.request.setRequestHeader('Content-Type', uploadFile.file.type);
      uploadFile.request.responseType = 'text';
      uploadFile.request.send(uploadFile.file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignUrls, presignedResults]);
  useEffect(() => {
    // abort upload requests when unmounted
    return () => {
      if (uploadFiles) {
        uploadFiles.forEach(uploadFile => uploadFile.request.abort());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    uploadFiles,
    isUploading,
    error: presignedError,
    upload,
    remove,
    removeAll
  };
}
/**
 * Support getting multiple presigned urls for bulk uploading
 */
export function useCreatePresignedUrls(
  path: string,
  presignEndPoint = defaultPresignEndPoint
) {
  const { isLoading, post, clearData, data, error } =
    useApiPost<FilePresignedDto>(`${presignEndPoint}/${path}`);
  const filesRef = useRef<File[]>([]);
  const nextIndexRef = useRef(0);
  const [presignedResults, setPresignedResults] = useState<FilePresignedDto[]>(
    []
  );
  const presignUrls = useCallback(
    (files?: File[]) => {
      if (files) {
        // add files to get presigned urls
        filesRef.current.push(...files);
      }
      // get presigned url for next item in queue
      if (nextIndexRef.current < filesRef.current.length && !isLoading) {
        log(
          'presign post',
          stringify({
            mimeType: filesRef.current[nextIndexRef.current].type
          })
        );
        post({ mimeType: filesRef.current[nextIndexRef.current].type });
      }
    },
    [isLoading, post]
  );
  useEffect(() => {
    // check if presigned url has returned and proceed to next file
    if (data && !isLoading) {
      const file = filesRef.current[nextIndexRef.current];
      setPresignedResults([...presignedResults, { ...data, file }]);
      nextIndexRef.current++;
      presignUrls(); // process next file
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);
  // console.log('presignedResults', presignedResults);
  return { presignedResults, isLoading, error, presignUrls, clearData };
}
